import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { ArrowClockwise, Play, SealCheck, X } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { useTranslation } from "@/contexts/LanguageContext";
import { useQuizBadges } from "@/contexts/QuizBadgesContext";
import PracticeStreakBadge from "@/components/quiz/PracticeStreakBadge";
import { getLessonQuiz } from "@/data/quizzes";
import LessonQuiz from "@/components/quiz/LessonQuiz";

// Practice only the missed exam questions; wrong ones come back until all are right.
export default function PracticeRound({ refs, onExit, onStartExam }) {
  const { lang } = useTranslation();
  const es = lang === "es";
  const [queue, setQueue] = useState(refs);
  const [round, setRound] = useState(1);
  const [pending, setPending] = useState(null);
  const questions = useMemo(() => queue.map((r) => getLessonQuiz(r.path, lang)[r.i]), [queue, lang]);
  const done = pending && pending.length === 0;
  const { practiceClears, recordPracticeClear } = useQuizBadges();

  useEffect(() => {
    if (!done) return;
    recordPracticeClear().then((n) => {
      if (n) toast.success(es ? `¡Insignia Racha de Práctica ×${n}!` : `Practice Streak badge ×${n}!`);
    });
  }, [done]); // eslint-disable-line react-hooks/exhaustive-deps

  const onFinish = (score, total, missedIdx) => setPending(missedIdx.map((i) => queue[i]));
  const next = () => {
    setQueue(pending);
    setPending(null);
    setRound((r) => r + 1);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <section className="space-y-4" data-testid="practice-round">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h2 className="font-['Outfit'] text-white text-xl font-semibold">{es ? "Práctica: tus preguntas falladas" : "Practice: your missed questions"}</h2>
          <p className="text-[#71717A] text-sm" data-testid="practice-status">
            {es ? `Ronda ${round} · ${queue.length} ${queue.length === 1 ? "pregunta" : "preguntas"} · las incorrectas vuelven hasta que aciertes todas.` : `Round ${round} · ${queue.length} ${queue.length === 1 ? "question" : "questions"} · wrong answers come back until you get them all right.`}
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={onExit} className="border-white/15 text-[#D4D4D8] hover:bg-white/5" data-testid="practice-exit-btn">
          <X weight="bold" className="w-4 h-4 mr-1.5" />
          {es ? "Salir de la práctica" : "Exit practice"}
        </Button>
      </div>

      <LessonQuiz key={`${round}-${lang}`} questions={questions} testPrefix="practice" onFinish={onFinish} allowRetry={false} />

      {pending && !done && (
        <div className="flex items-center justify-between gap-3 flex-wrap bg-[#F5A524]/5 border border-[#F5A524]/30 rounded-xl p-4" data-testid="practice-next">
          <p className="text-[#D4D4D8] text-sm">
            {es ? `Te faltan ${pending.length}. Lee las explicaciones y vuelve a intentarlo.` : `${pending.length} still to get right. Read the explanations, then go again.`}
          </p>
          <Button onClick={next} className="bg-[#F5A524] text-[#0A0A0A] hover:bg-[#F5B041] font-medium" data-testid="practice-next-btn">
            <ArrowClockwise weight="bold" className="w-4 h-4 mr-1.5" />
            {es ? `Practicar ${pending.length} de nuevo` : `Practice ${pending.length} again`}
          </Button>
        </div>
      )}

      {done && (
        <div className="flex items-center justify-between gap-3 flex-wrap bg-emerald-500/5 border border-emerald-500/30 rounded-xl p-4" data-testid="practice-done">
          <p className="flex items-center gap-2 text-emerald-300 font-medium flex-wrap">
            <SealCheck weight="fill" className="w-5 h-5" />
            {es ? "¡Todas correctas! Ya estás listo para el examen completo." : "All correct! You're ready for the full exam."}
            <PracticeStreakBadge count={practiceClears} testId="practice-done-streak" />
          </p>
          <Button onClick={onStartExam} className="bg-[#F5A524] text-[#0A0A0A] hover:bg-[#F5B041] font-semibold" data-testid="practice-start-exam-btn">
            <Play weight="fill" className="w-4 h-4 mr-1.5" />
            {es ? "Volver a presentar el examen" : "Retake the exam"}
          </Button>
        </div>
      )}
    </section>
  );
}
